import { useState } from 'react';

import { EmojiClickData } from 'emoji-picker-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { ConditionField } from '@/components/organisms/ConditionField/ConditionField';
import {
  EMPTY_CONDITION,
  VIEW_CONDITION_ATTRIBUTES,
} from '@/components/organisms/ConditionField/constants';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useDeskId } from '@/hooks/useDeskId';
import { useViews } from '@/hooks/useViews';
import { popModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { Condition } from '@/shared/types/condition';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { ViewType } from '@/shared/types/views';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { capitalizeFirstLetter } from '@/util/util';

import { EmojiInput } from './EmojiInput/EmojiInput';
import { schema } from './schemas';

const DEFAULT_ICON = '/assets/white_flag_emoji.png';

type Form = { name: string; icon: string; type: ViewType; condition: Condition };

type ModalViewsProps = { viewId?: string };

export const ModalViews = ({ viewId }: ModalViewsProps) => {
  const dispatch = useDispatch();
  const { updateView, createView, isLoading, isViewOwner, view } = useViews(viewId);
  const deskId = useDeskId();
  const { t } = useTranslation();
  const [isEmojiMenuOpen, setEmojiMenuOpen] = useState(false);

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'views', actions.WRITE));

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<Form>({
    defaultValues: {
      name: viewId ? view.name : '',
      type: viewId ? view.type : ViewType.PUBLIC,
      icon: viewId ? view.icon : DEFAULT_ICON,
      condition: view?.condition ?? EMPTY_CONDITION,
    },
    mode: 'onChange',
    resolver: createYupResolver<Form>(schema(t, deskId, view?.name)),
  });

  const nameErrorMessage = capitalizeFirstLetter(errors?.name?.message) as string;
  const conditionErrorMessage = isSubmitted ? errors.condition?.message : undefined;

  const onClose = () => {
    dispatch(popModal());
  };

  const onSubmit = (form: Form) => {
    if (viewId) {
      // Users that are not the owner of the view can't change the type
      const { type: _type, ...rest } = form;
      trackEvent(EventName.UpdateView, { view_id: viewId });
      updateView({ view_id: viewId, view: isViewOwner ? form : rest }, { onSuccess: onClose });
    } else {
      trackEvent(EventName.CreateView);
      createView(form, { onSuccess: onClose });
    }
  };

  const handleSetEmoji = (emoji: EmojiClickData) => {
    setValue('icon', emoji.imageUrl);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isEmojiMenuOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        onPointerDownOutside={(event) => {
          if (isEmojiMenuOpen) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (isEmojiMenuOpen) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              viewId
                ? 'conversation.views.modal_title_edit'
                : 'conversation.views.modal_title_create'
            )}
          </DialogTitle>
          <DialogDescription>{t('conversation.views.modal_description')}</DialogDescription>
        </DialogHeader>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <EmojiInput
              field={field}
              label={t('field.name')}
              placeholder={t('conversation.views.modal_placeholder')}
              onEmojiClick={handleSetEmoji}
              error={Boolean(errors?.name?.message)}
              errorMessage={nameErrorMessage}
              emojiPlaceholder={view?.icon}
              setEmojiMenuOpen={setEmojiMenuOpen}
              isEmojiMenuOpen={isEmojiMenuOpen}
            />
          )}
        />
        {isViewOwner && (
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <Label className="cursor-pointer items-start">
                <Checkbox
                  className="mt-0.5"
                  checked={field.value === ViewType.PRIVATE}
                  onCheckedChange={(checked) =>
                    field.onChange(checked === true ? ViewType.PRIVATE : ViewType.PUBLIC)
                  }
                />
                <span className="flex flex-col gap-0.5">
                  {t('conversation.views.private_view')}
                  <span className="font-normal text-foreground-muted">
                    {t('conversation.views.private_view_description')}
                  </span>
                </span>
              </Label>
            )}
          />
        )}
        <div className="flex flex-col gap-2">
          <FieldHeader title={t('common.filters')} error={conditionErrorMessage} />
          <Controller
            control={control}
            name="condition"
            render={({ field }) => (
              <ConditionField
                value={field.value}
                onChange={field.onChange}
                attributes={VIEW_CONDITION_ATTRIBUTES}
                disabled={!canWrite}
                showErrors={isSubmitted}
              />
            )}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} isLoading={isLoading}>
            {viewId ? t('common.save') : t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
